import type { GeneralInfoPicklistItem, GeneralInfoPicklistsProp } from '../api/generalInfoApi';
import { useEffect, useMemo, useRef } from 'react';
import { localizeAssetFieldLabel, useI18n } from '../i18n';
import { israelSettlementWireValue } from '../utils/israelSettlements';
import { findIsraelSettlementByWire } from '../utils/israelSettlements';
import {
  applyIsraelAddressGeoPatch,
  buildIsraelAddressGeoLabels,
  geocodeIsraelAddressZip,
  resolveIsraelGeoFromCity,
} from '../utils/resolveIsraelAddressGeo';
import { IsraelSettlementCitySelect } from './IsraelSettlementCitySelect';
import { IsraelSettlementStreetSelect } from './IsraelSettlementStreetSelect';
import formStyles from './DynamicRecordForm.module.css';

type IsraelAddressFieldsetProps = {
  data: Record<string, unknown>;
  path: string[];
  idPrefix: string;
  onFieldChange: (path: string[], value: unknown) => void;
  generalInfoPicklists?: GeneralInfoPicklistsProp;
  mandatoryMarkerKeys?: readonly string[];
  readonlyPathPredicate?: (pathSegments: readonly string[]) => boolean;
  theme?: 'dark' | 'light';
  compactLayout?: boolean;
};

function pickString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  return '';
}

/** Preserve spaces while typing in free-text address fields. */
function pickDisplayString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function fieldRowClassName(compactLayout: boolean): string {
  return compactLayout ? `${formStyles.fieldRow}` : formStyles.fieldRow;
}

function isReadonly(
  path: string[],
  key: string,
  readonlyPathPredicate?: (pathSegments: readonly string[]) => boolean,
): boolean {
  return Boolean(readonlyPathPredicate?.([...path, key]));
}

function isMandatoryAddressField(
  path: string[],
  key: string,
  mandatoryMarkerKeys?: readonly string[],
): boolean {
  return showMandatoryMarker(path, key, mandatoryMarkerKeys);
}

function showMandatoryMarker(
  path: readonly string[],
  fieldKey: string,
  markerKeys?: readonly string[],
): boolean {
  if (!markerKeys?.length) {
    return false;
  }
  const dotted = [...path, fieldKey].join('.');
  if (markerKeys.includes(dotted)) {
    return true;
  }
  return path.length === 0 && markerKeys.includes(fieldKey);
}

export function IsraelAddressFieldset({
  data,
  path,
  idPrefix,
  onFieldChange,
  generalInfoPicklists,
  mandatoryMarkerKeys,
  readonlyPathPredicate,
  theme = 'dark',
  compactLayout = false,
}: IsraelAddressFieldsetProps) {
  const { t } = useI18n();
  const geocodeTimerRef = useRef<number | null>(null);

  const city = pickDisplayString(data.city);
  const street = pickDisplayString(data.street);
  const building = pickDisplayString(data.building);
  const zipCode =
    pickString(data.zip_code) || pickString(data.postal_code) || pickString(data.zip);
  const address2 = pickDisplayString(data.address2);

  const regions = generalInfoPicklists?.regions ?? [];
  const countries = generalInfoPicklists?.countries ?? [];

  const geoLabels = useMemo(() => {
    const geo = resolveIsraelGeoFromCity(pickString(city), regions);
    return buildIsraelAddressGeoLabels(regions, geo.stateId, countries);
  }, [city, countries, regions]);

  const applyGeoFromCity = (nextCity: string, clearStreet: boolean) => {
    const geo = resolveIsraelGeoFromCity(nextCity, regions);
    onFieldChange([...path, 'city'], nextCity);
    if (clearStreet) {
      onFieldChange([...path, 'street'], '');
      onFieldChange([...path, 'building'], '');
      onFieldChange([...path, 'zip_code'], '');
    }
    onFieldChange(['country_id'], geo.countryId);
    if (geo.stateId != null) {
      onFieldChange(['state_id'], geo.stateId);
    }
  };

  const onCityChange = (nextCity: string) => {
    const settlement = findIsraelSettlementByWire(nextCity);
    const wire = settlement ? israelSettlementWireValue(settlement) : nextCity;
    const cityChanged = wire !== city;
    applyGeoFromCity(wire, cityChanged);
  };

  useEffect(() => {
    const cityTrimmed = pickString(city);
    const streetTrimmed = pickString(street);
    if (!cityTrimmed || !streetTrimmed) {
      return undefined;
    }
    if (geocodeTimerRef.current != null) {
      window.clearTimeout(geocodeTimerRef.current);
    }
    geocodeTimerRef.current = window.setTimeout(() => {
      void geocodeIsraelAddressZip(
        {
          city: pickString(city),
          street: pickString(street),
          building: pickString(building),
        },
        geoLabels,
      ).then((postalCode) => {
          if (!postalCode || pickString(data.zip_code)) {
            return;
          }
          onFieldChange([...path, 'zip_code'], postalCode);
        },
      );
    }, 700);
    return () => {
      if (geocodeTimerRef.current != null) {
        window.clearTimeout(geocodeTimerRef.current);
      }
    };
  }, [building, city, street, geoLabels, data.zip_code, onFieldChange, path]);

  const rowClass = fieldRowClassName(compactLayout);
  const cityId = `${idPrefix}-address-city`;
  const streetId = `${idPrefix}-address-street`;
  const buildingId = `${idPrefix}-address-building`;
  const zipId = `${idPrefix}-address-zip`;
  const address2Id = `${idPrefix}-address-address2`;

  return (
    <div className={compactLayout ? formStyles.nestedFieldsCompact : formStyles.nestedFields}>
      <IsraelSettlementCitySelect
        id={cityId}
        label={localizeAssetFieldLabel('city', t)}
        value={city}
        onChange={onCityChange}
        readOnly={isReadonly(path, 'city', readonlyPathPredicate)}
        disabled={isReadonly(path, 'city', readonlyPathPredicate)}
        mandatory={isMandatoryAddressField(path, 'city', mandatoryMarkerKeys)}
        theme={theme}
        rowClassName={rowClass}
      />

      <IsraelSettlementStreetSelect
        id={streetId}
        label={localizeAssetFieldLabel('street', t)}
        cityWire={city}
        value={street}
        onChange={(next) => {
          onFieldChange([...path, 'street'], next);
          if (next !== street) {
            onFieldChange([...path, 'zip_code'], '');
          }
        }}
        readOnly={isReadonly(path, 'street', readonlyPathPredicate)}
        disabled={isReadonly(path, 'street', readonlyPathPredicate)}
        mandatory={isMandatoryAddressField(path, 'street', mandatoryMarkerKeys)}
        theme={theme}
        rowClassName={rowClass}
      />

      <div className={rowClass}>
        <label className={formStyles.inputLabel} htmlFor={buildingId}>
          {localizeAssetFieldLabel('building', t)}
          {isMandatoryAddressField(path, 'building', mandatoryMarkerKeys) ? (
            <span className={formStyles.mandatoryAsterisk} aria-hidden="true"> *</span>
          ) : null}
        </label>
        <input
          id={buildingId}
          className={formStyles.textInput}
          type="text"
          inputMode="text"
          autoComplete="off"
          value={building}
          disabled={isReadonly(path, 'building', readonlyPathPredicate) || !city.trim()}
          readOnly={isReadonly(path, 'building', readonlyPathPredicate)}
          placeholder={t('form.picklist.buildingPlaceholder')}
          aria-required={isMandatoryAddressField(path, 'building', mandatoryMarkerKeys) || undefined}
          onChange={(event) => onFieldChange([...path, 'building'], event.target.value)}
        />
      </div>

      {'address2' in data ? (
        <div className={rowClass}>
          <label className={formStyles.inputLabel} htmlFor={address2Id}>
            {localizeAssetFieldLabel('address2', t)}
          </label>
          <input
            id={address2Id}
            className={formStyles.textInput}
            type="text"
            value={address2}
            readOnly={isReadonly(path, 'address2', readonlyPathPredicate)}
            disabled={isReadonly(path, 'address2', readonlyPathPredicate)}
            onChange={(event) => onFieldChange([...path, 'address2'], event.target.value)}
          />
        </div>
      ) : null}

      <div className={rowClass}>
        <label className={formStyles.inputLabel} htmlFor={zipId}>
          {localizeAssetFieldLabel('zip_code', t)}
        </label>
        <input
          id={zipId}
          className={formStyles.textInput}
          type="text"
          value={zipCode}
          readOnly
          disabled
          placeholder={t('form.picklist.zipAutoPlaceholder')}
          aria-describedby={`${zipId}-hint`}
        />
        <p className={formStyles.arrayHint} id={`${zipId}-hint`}>
          {t('form.picklist.zipAutoHint')}
        </p>
      </div>
    </div>
  );
}

/** Exported for tests — apply geo ids + zip without React. */
export function patchPayloadFromIsraelAddress(
  body: Record<string, unknown>,
  regions: readonly GeneralInfoPicklistItem[],
  zipCode?: string | null,
): Record<string, unknown> {
  const addr = body.address;
  const city =
    addr && typeof addr === 'object' && !Array.isArray(addr)
      ? pickString((addr as Record<string, unknown>).city)
      : '';
  const geo = resolveIsraelGeoFromCity(city, regions);
  return applyIsraelAddressGeoPatch(body, {
    countryId: geo.countryId,
    stateId: geo.stateId,
    zipCode: zipCode ?? null,
  });
}
